import React, { Fragment, useEffect, useState } from 'react'
import { Box, Collapse, Grid, Radio, RadioGroup, FormControlLabel, FormControl, Theme } from '@mui/material'
import { makeStyles } from '@mui/styles'
import { useDispatch } from 'react-redux'
import { FormProps } from '../../../types/default-types'
import { Modal, Snackbar, SubmitButton } from '../../../components'
import { getRequestList, requestRegister } from '../../../api'
import { modalsAction } from '../../../store/modals'

export const CreateRequestModal: React.FC<{ show: boolean; updateInfo(): void; onClose: () => void }> = ({
  show = false,
  updateInfo,
  onClose = () => {},
}): JSX.Element => {
  const classes = useStyles()

  const dispatch = useDispatch()
  const [value, setValue] = React.useState('')

  const defaultValue = {
    1: false,
    2: false,
    3: false,
    4: false,
  }

  const [formProps, setFormProps] = useState<FormProps<any>>({
    data: defaultValue,
    processed: false,
    snackbar: {
      message: undefined,
      onClose: () => setFormProps({ ...formProps, snackbar: { ...formProps.snackbar, message: undefined } }),
    },
  })

  const { 1: answer1, 2: answer2, 3: answer3, 4: answer4 } = formProps.data

  const error = [answer1, answer2, answer3, answer4].filter((v) => v).length === 0

  const [collapse, setCollapse] = useState<boolean>(true)
  const [requestList, setRequestList] = useState<any>({
    data: [],
  })

  const handleRadioChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setValue((event.target as HTMLInputElement).value)
    setFormProps({
      ...formProps,
      data: { ...defaultValue, [event.target.value]: true },
      snackbar: { ...formProps.snackbar, message: undefined },
    })
  }

  const onSubmitForm = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault()

    setFormProps({ ...formProps, processed: true })

    if (error) {
      setFormProps({
        ...formProps,
        processed: false,
        snackbar: {
          ...formProps.snackbar,
          message: { error: ['Вы не выбрали ни одной номинации для подачи заявки.'] },
        },
      })
    } else {
      requestRegister({ nominationId: Number(value) })
        .then((res) => {
          const { status = 0, message: messages } = res

          setFormProps({ ...formProps, processed: false })

          if (status === 1) {
            dispatch(
              modalsAction.alert({
                title: 'Поздравляем',
                actionButtonText: 'Продолжить',
                message: { success: [`${messages}`] },
                onClose: () => {
                  updateInfo()
                  onCloseModal()
                },
              })
            )
          } else {
            setFormProps({
              ...formProps,
              processed: false,
              snackbar: { ...formProps.snackbar, message: { error: [`${messages}`] } },
            })
          }
        })
        .catch(() => {
          setFormProps({
            ...formProps,
            processed: false,
            snackbar: { ...formProps.snackbar, message: { error: ['Ошибка сервера. Попробуйте позже'] } },
          })
        })
    }
  }

  const onCloseModal = () => {
    onClose()
    setCollapse(false)
    setFormProps({ ...formProps, data: defaultValue })
  }

  useEffect(() => {
    if (!collapse) {
      setTimeout(() => setCollapse(true), 200)
    }
  }, [collapse])

  useEffect(() => {
    getRequestList().then((res) => {
      const { status = 0 } = res
      if (status === 1) setRequestList({ data: res.data.nomination })
    })
  }, [])

  const check = {
    icon: <span className={classes.icon} />,
    checked: (
      <span className={classes.icon}>
        <Box className={classes.check} />
      </span>
    ),
  }

  return (
    <Modal
      open={show}
      maxWidth="sm"
      title="Выберите номинацию для заявки"
      onClose={onCloseModal}
      className={classes.modal}
    >
      <Collapse in={collapse}>
        <Box mt={3}>
          <form onSubmit={onSubmitForm}>
            <Grid container justifyContent="center">
              <Grid item xs="auto">
                <Box mb={2}>
                  <FormControl error={error}>
                    <RadioGroup value={value} onChange={handleRadioChange} name="radio-buttons-group">
                      {requestList.data &&
                        requestList.data.map((item: any) => {
                          return (
                            <Fragment key={item.id}>
                              <FormControlLabel
                                name={`${item.id}`}
                                value={`${item.id}`}
                                control={
                                  <Radio
                                    color="secondary"
                                    checked={formProps.data[`${item.id}`]}
                                    checkedIcon={check.checked}
                                    icon={check.icon}
                                  />
                                }
                                label={`«${item.name}»`}
                              />
                            </Fragment>
                          )
                        })}
                    </RadioGroup>
                  </FormControl>
                </Box>
              </Grid>
            </Grid>
            <Grid container justifyContent="center">
              <Grid item xs={12} sm={10}>
                <Box className={classes.info}>
                  Внимание! После подачи заявки поменять выбор будет невозможно. Подать заявку повторно можно после
                  отклонения вашей заявки руководителем.
                </Box>
              </Grid>
            </Grid>
            <Grid container justifyContent="center">
              <Grid item xs={12} sm={10}>
                <Snackbar {...formProps.snackbar} variantMessage="error" />
              </Grid>
            </Grid>
            <Box width="100%" maxWidth={200} mx="auto" mt={2} mb={1}>
              <SubmitButton
                fullWidth
                disabled={formProps.processed}
                processed={formProps.processed}
                title="Продолжить"
                className={classes.btn}
              />
            </Box>
          </form>
        </Box>
      </Collapse>
    </Modal>
  )
}

const useStyles = makeStyles(({ spacing, font, breakpoints: { down }, palette }: Theme) => ({
  modal: {
    '& .MuiDialogTitle-root': {
      fontSize: '24px!important',
      paddingLeft: '60px!important',
      paddingRight: '60px!important',
      [down('sm')]: {
        fontSize: '22px!important',
        paddingLeft: '40px!important',
        paddingRight: '40px!important',
      },
    },
    '& .MuiFormControlLabel-root .MuiTypography-root': {
      fontSize: '15px!important',
      fontWeight: '600!important',
      color: `${palette.text.secondary}!important`,
      [down('sm')]: {
        fontSize: '14px!important',
      },
    },
  },
  btn: {
    fontSize: '16px!important',
    padding: '10px 30px!important',
    minHeight: '35px!important',
  },
  icon: {
    width: 10,
    height: 10,
    display: 'flex',
    borderRadius: '50%',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: 'transparent',
    border: `3px solid ${palette.primary.dark}`,
  },
  check: {
    borderRadius: 50,
    width: 10,
    height: 10,
    backgroundColor: ` ${palette.secondary.main}`,
  },
  info: {
    fontSize: 15,
    lineHeight: 1.4,
    textAlign: 'center',
    color: palette.text.secondary,
    margin: '10px 0 20px',
  },
}))
